// Корневой Provider приложения — обёртка над ThemeModeProvider + CssBaseline + LocalizationProvider + AuthProvider + QueryClient.
// Используется в main.tsx для оборачивания <RouterProvider>.
// Порядок: ThemeMode → CssBaseline → LocalizationProvider → QueryClient → AuthProvider → children (RouterProvider).
// AuthProvider должен быть выше RouterProvider, потому что ProtectedRoute/RoleGuard
// используют useAuth для проверки статуса аутентификации.
// QueryClient должен быть выше всех компонентов, использующих useQuery/useMutation.
// LocalizationProvider (MUI X DatePickers) — обёртывает date-fns adapter с ru locale.
import { useMemo, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ru as ruLocale } from 'date-fns/locale';
import { ThemeModeProvider } from './index';
import { AuthProvider } from '@/auth/AuthContext';

/** Provider-обёртка для всего приложения. Подключает MUI Theme + CssBaseline + QueryClient + Auth. */
export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  // Создаём QueryClient один раз на всё приложение. devtools пока не подключаем (production-grade).
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000, // 30 секунд — типичный баланс между свежестью и нагрузкой.
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );

  return (
    <ThemeModeProvider>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ruLocale}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>{children}</AuthProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeModeProvider>
  );
}
